/**
 * unsavedWork — may "New mindmap" clear the canvas without asking?
 *
 * It used to, always: one click on ☰ → New mindmap instantly reset the scene,
 * and an unsaved scratch canvas (which lives only in localStorage, and is
 * overwritten with the empty scene moments later) was gone. No confirm, no
 * undo. This is the guard.
 *
 * Cases:
 *  - empty canvas                    → just start; there is nothing to lose.
 *  - scratch canvas with content     → confirm; the work exists nowhere but
 *                                      this browser, and resetting destroys it.
 *  - cloud file, everything saved    → just start; the work is safe in the
 *                                      cloud and reachable from My Files.
 *  - cloud file with unsaved edits   → confirm; the pending edits have not
 *                                      reached the server yet.
 *  - cloud file open, but real work  → confirm; while a cloud file is open the
 *    still sits in scratch storage     scratch canvas is untouched in
 *                                      localStorage, and "New mindmap" calls
 *                                      clearScratchScene() — that browser-only
 *                                      scene would be gone (finding #6).
 */

export type NewMindmapDecision = "start" | "confirm";

/**
 * What the confirmation is protecting, so the dialog can say something true.
 *
 *  - `scratch`               — on the scratch canvas; the live canvas is the
 *                              unsaved work.
 *  - `cloudFile`             — the open cloud file has edits that never reached
 *                              the cloud. Saving first genuinely rescues them.
 *  - `scratchBehindCloudFile`— a cloud file is open and fully synced; the only
 *                              thing at risk is the stored scratch scene. There
 *                              is NO save that rescues it (Save-to-cloud saves
 *                              the on-screen scene, i.e. the cloud file), so the
 *                              dialog must offer discard/cancel only. Offering
 *                              "Save it first" here used to claim the cloud file
 *                              was unsaved and then destroy the scratch scene it
 *                              had just warned about.
 */
export type UnsavedWorkKind =
  | "scratch"
  | "cloudFile"
  | "scratchBehindCloudFile";

export interface CanvasSaveState {
  /** The canvas has at least one non-deleted element. */
  hasContent: boolean;
  /** A cloud file is open (or pending creation), rather than the scratch canvas. */
  isCloudContext: boolean;
  /** The open cloud file has edits that have not reached the cloud. */
  hasUnsavedCloudChanges: boolean;
  /**
   * The stored scratch scene (localStorage) has real, non-deleted content. Only
   * meaningful in a cloud context: with a cloud file open the scratch canvas is
   * left untouched in localStorage and starting a new mindmap wipes it, so it is
   * a second thing "New mindmap" can silently destroy (finding #6). On the
   * scratch canvas itself this is redundant with `hasContent` (the live canvas
   * IS that scene) and is ignored.
   */
  scratchHasContent: boolean;
}

/** Is the work currently ON SCREEN lost by a reset? */
const currentCanvasAtRisk = ({
  hasContent,
  isCloudContext,
  hasUnsavedCloudChanges,
}: CanvasSaveState): boolean =>
  isCloudContext
    ? // A cloud file's on-screen work is only at risk if it hasn't reached the
      // cloud yet; an empty cloud canvas has nothing to lose even if flagged.
      hasContent && hasUnsavedCloudChanges
    : // Scratch canvas: the live canvas is unsaved, browser-only work.
      hasContent;

/** Is the STORED scratch scene lost by a reset? (Only in a cloud context.) */
const scratchAtRisk = ({
  isCloudContext,
  scratchHasContent,
}: CanvasSaveState): boolean => isCloudContext && scratchHasContent;

export const decideNewMindmap = (state: CanvasSaveState): NewMindmapDecision =>
  // Confirm whenever starting fresh would lose meaningful work from EITHER the
  // current canvas or the stored scratch scene that a reset would clear.
  currentCanvasAtRisk(state) || scratchAtRisk(state) ? "confirm" : "start";

/**
 * Name what the confirmation is protecting. When BOTH the cloud file and the
 * stored scratch scene are at risk we report `cloudFile`: saving first is a real
 * option there (it rescues the cloud edits), even though the scratch scene is
 * still lost — that residual is not something a single "save" can fix.
 */
export const unsavedWorkKind = (state: CanvasSaveState): UnsavedWorkKind => {
  if (!state.isCloudContext) {
    return "scratch";
  }
  return currentCanvasAtRisk(state) ? "cloudFile" : "scratchBehindCloudFile";
};
